import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate, type VersionReadyEvent } from '@angular/service-worker';
import { filter, first } from 'rxjs/operators';
import { concat, interval } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  readonly #swUpdate = inject(SwUpdate);
  readonly #appRef = inject(ApplicationRef);

  constructor() {
    if (this.#swUpdate.isEnabled) {
      this.#setupUpdateHandling();
      this.#checkPeriodicUpdates();
    }
  }

  #setupUpdateHandling(): void {
    this.#swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'NO_NEW_VERSION_DETECTED'))
      .subscribe(() => {
        console.log('Nincs elérhető frissítés');
      });

    this.#swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(event => {
        console.log(`Jelenlegi verzió: ${event.currentVersion.hash}`);
        console.log(`Új verzió elérhető: ${event.latestVersion.hash}`);
        this.activateUpdate();
      });

    // Hibák kezelése
    this.#swUpdate.versionUpdates
      .pipe(filter(evt => evt.type === 'VERSION_INSTALLATION_FAILED'))
      .subscribe(event => {
        console.error('Frissítés sikertelen:', event.error);
      });

    // Helyreállíthatatlan hibák kezelése
    this.#swUpdate.unrecoverable.subscribe(event => {
      console.error('Helyreállíthatatlan hiba:', event.reason);
    });
  }

  #checkPeriodicUpdates(): void {
    // Az app stabil állapotát várjuk meg
    const appIsStable$ = this.#appRef.isStable.pipe(first(isStable => isStable === true));

    // 6 óránként ellenőrizzük az új verziókat
    const everySixHours$ = interval(6 * 60 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

    everySixHoursOnceAppIsStable$.subscribe(async () => {
      try {
        await this.#swUpdate.checkForUpdate();
      } catch (error) {
        console.error('Frissítés ellenőrzési hiba:', error);
      }
    });
  }

  async activateUpdate(): Promise<void> {
    try {
      const updated = await this.#swUpdate.activateUpdate();
      if (updated) {
        console.log('App frissítve, bongeszo frissites szukseges');
      }
    } catch (error) {
      console.error('Frissítés aktiválási hiba:', error);
    }
  }
}
